import '../icon/ui-icon';
import { UIElement } from '../ui-element';
import { scrollTo } from '../../global/helpers';
import {
    createElement,
    closestByCond,
    html,
    element,
    getCSSPropertyValue,
    extractSpecificElement,
    warnForAttributeDeprecation,
} from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { browser } from '../../global/browser';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Labels } from '../../global/labels';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    AttributeValue,
    ObservedAttributes,
    TagsList,
} from '../ui-element.type';
import type {
    ContentNodes,
    EventHandler,
    MaybeElement,
    NullOr,
    ResizeHandler,
    UndefinedOr,
} from '../../global/utils.type';
import { UIIcon } from '../icon/ui-icon';
import type {
    UICurtainCustomEvents,
    UICurtainLayout,
    UICurtainLayouts,
} from './ui-curtain.type';
import type { UIIconGlyph, UIIconGlyphRedesign } from '../icon/ui-icon.types';
import type { UIAmount } from '../amount/ui-amount';
import styles from './ui-curtain.css';

/**
 * @class
 * @memberof SharedComponents
 * @implements {Activatable}
 * @augments {UIElement}
 * @classdesc Represents a class for <code>ui-curtain</code> element.
 * Curtain is opening section with content. Layout 'spoiler'
 * can be without container ui-curtains.
 * @fires event:curtain-toggle
 * @fires event:curtain-open
 * @fires event:curtain-close
 * @property {UICurtainLayout} layout {@attr layout} Type of layout.
 *  {@desc default}
 *  {@desc spoiler}
 * @property {boolean} [limited=false] {@attr limited} Deprecated: If true, curtain will not
 * be active. This property will be removed in next releases.
 * @property {boolean} [mobile] {@attr mobile} Curtain is shown only in smaller screen view.
 * @property {boolean} [autoscroll=false] {@attr autoscroll} If true: user scrolled to the current curtain after opening.
 * @property {boolean} [hideHead] {@attr hide-head} If true, curtain's head is hidden.
 * @property {string} [glyph] {@attr glyph} Glyph for icon on the left side (not used in spoiler)
 * @property {string} [label] {@attr label} Label of curtain.
 * @property {string} [labelOpen] {@attr label-open} Label of open button,
 * used in layout="spoiler".
 * @property {string} [labelClose] {@attr label-close} Label of close button,
 * used in layout="spoiler".
 * @property {boolean} [open=false] {@attr open} If true, curtain will be opened,
 * otherwise closed.
 * @property {number} [index] {@attr index} Index of numbered curtain (not used in spoiler)
 * @property {boolean} [completed=false] {@attr completed} If true, numbered curtain receive completed layout (not used in spoiler)
 * @property {boolean} [disabled] {@attr disabled} Special state for spoiler: if true - disabled (not used in curtain)
 * @property {HTMLDetailsElement} details Shortcut to curtain's main element.
 * @property {HTMLElement} head Shortcut to curtain's of head element.
 * @property {UIIcon} headIcon Shortcut to curtain's icon on the left of head-content.
 * @property {HTMLSpanElement} headTitle Shortcut to curtain's title in head-content.
 * @property {HTMLDivElement} headContent Shortcut to curtain's head-content element.
 * @property {HTMLDivElement} headScreenReaderHint Shortcut to curtain's head screen reader hint.
 * @property {HTMLDivElement} body Shortcut to curtain's body element.
 * @property {HTMLDivElement} content Shortcut to curtain's body's content element.
 * @alias UICurtain
 * @element ui-curtain
 * @slot
 * @example
 * <ui-curtain label="Open me">
 *   <p>Very interesting content</p>
 * </ui-curtain>
 */
@Component({ tag: 'ui-curtain', styles })
export class UICurtain extends UIElement {
    /**
     * Layout type of curtains.
     * @type {UICurtainLayouts}
     */
    static readonly Layout: UICurtainLayouts = {
        Default: 'default',
        Spoiler: 'spoiler',
    } as const;

    /**
     * Types of custom events
     * @type {UICurtainCustomEvents}
     */
    static readonly CustomEvents: UICurtainCustomEvents = {
        CurtainOpen: 'curtain-open',
        CurtainClose: 'curtain-close',
        CurtainToggle: 'curtain-toggle',
    } as const;

    /**
     * Define labels that could be localized
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-curtain', {
            showLess: 'Show less',
            showMore: 'Show more',
            completedStep: 'Completed step',
        });
    }

    /**
     * @type {Array<string>}
     */
    static readonly clickableActions: TagsList = [
        'a',
        'ui-link',
        'button',
        'ui-button',
    ] as const;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'layout',
            'label',
            'label-open',
            'label-close',
            'open',
            'glyph',
            'disabled',
            'limited',
            'hide-head',
            'mobile',
            'index',
            'completed',
        ];
    }

    @Attribute(String) layout?: UICurtainLayout;
    @Attribute(String) glyph?: UIIconGlyph | UIIconGlyphRedesign;
    @Attribute(Boolean) mobile?: boolean;
    @Attribute(Boolean) autoscroll?: boolean;
    @Attribute(String) label?: string;
    @Attribute(String) labelOpen?: string;
    @Attribute(String) labelClose?: string;
    // null is not a good practice here, it is set only for backwards compatibility.
    @Attribute({ type: Number, default: null }) index?: number;
    @Attribute({
        type: Boolean,
        validate: warnForAttributeDeprecation,
    })
    limited?: boolean;
    @Attribute(Boolean) completed: boolean = false;
    @Attribute(Boolean) disabled: boolean = false;
    @Attribute(Boolean) hideHead: boolean = false;
    @Attribute(Boolean) open: boolean = false;

    @Child('details') details!: HTMLDetailsElement;
    @Child('.ui-curtain__head') head!: HTMLElement;
    @Child('.ui-curtain__head-icon') headIcon?: HTMLElement;
    @Child('.ui-curtain__head-title') headTitle!: HTMLSpanElement;
    @Child('.ui-curtain__head-content') headContent!: HTMLDivElement;
    @Child('.ui-curtain__head-content-sr-hint')
    headScreenReaderHint?: HTMLDivElement;
    @Child('.ui-curtain__body') body!: HTMLDivElement;
    @Child('.ui-curtain__body-content') content!: HTMLDivElement;

    private animation: NullOr<Animation> = null;
    private isClosing: boolean = false;
    private isExpanding: boolean = false;
    private _uuid?: string;
    private clickHandler?: EventHandler;
    private toggleHandler?: EventHandler;
    private resizeHandler?: ResizeHandler;

    /**
     * @returns {HTMLElement}
     */
    get toggle(): HTMLElement {
        return this.head;
    }

    /**
     * Disable animation for '(prefers-reduced-motion: reduce)'
     * @returns {boolean}
     */
    isAnimated(): boolean {
        return !browser.prefersReducedMotion();
    }

    /**
     * Checks if it's a spoiler
     * @returns {boolean}
     */
    isSpoiler(): boolean {
        return this.layout === UICurtain.Layout.Spoiler;
    }

    /**
     * Checks if it's
     * @returns {boolean}
     */
    isNumbered(): boolean {
        return !this.isSpoiler() && (this.index != null || this.completed);
    }

    /**
     * Checks if it's a spoiler
     * @returns {boolean}
     */
    isDisabledSpoiler(): boolean {
        return this.isSpoiler() && this.disabled;
    }

    /**
     * Checks if the head should be hidden
     * @returns {boolean}
     */
    isHeadHidden(): boolean {
        return (!browser.mobileMediaMatches() && this.mobile) || this.hideHead;
    }

    /**
     * Get text for spoiler title (opened/closed).
     * Custom attribute value or default translation label.
     * @returns {string}
     */
    getTitle(): string {
        if (this.label) {
            return this.label;
        }
        return this.open
            ? this.labelClose || String(UICurtain.labels.showLess)
            : this.labelOpen || String(UICurtain.labels.showMore);
    }

    /**
     * Build chevron accordingly to the curtain status
     * @returns {HTMLElement}
     */
    buildChevronElement(): HTMLElement {
        return element`
            <ui-icon
                class="ui-curtain__head-chevron"
                glyph="redesign-chevron-down"
            ></ui-icon>
        `;
    }

    /**
     * Build icon accordingly to the curtain attribute
     * @returns {HTMLElement}
     */
    buildIconElement(): UIIcon {
        return element`<ui-icon class='ui-curtain__head-icon' glyph="${this.glyph}"></ui-icon>`;
    }

    /**
     * Returns the numbered curtain's step hint for the screen readers.
     * @returns {string}
     */
    getScreenReaderStepHintText(): string {
        const currentIndex =
            !this.completed && this.index != null ? `#${this.index}, ` : '';

        return this.completed
            ? `${UICurtain.labels.completedStep}, `
            : currentIndex;
    }

    /**
     * Build numbered curtain list's index element for screen readers.
     * @returns {HTMLElement}
     */
    buildScreenReaderStepHintElement(): HTMLElement {
        return element`
            <span
                class="ui-curtain__head-content-sr-hint -visually-hidden"
            >${this.getScreenReaderStepHintText()}</span>
        `;
    }

    /**
     * Inserts the screen reader step hint to the header.
     */
    insertScreenReaderStepHint(): void {
        this.headContent.prepend(this.buildScreenReaderStepHintElement());
    }

    /**
     * Removes the screen reader step hint from the header.
     */
    removeScreenReaderStepHint(): void {
        if (this.headScreenReaderHint) {
            this.headContent.removeChild(this.headScreenReaderHint);
        }
    }

    /**
     * Updates the screen reader step hint in the header.
     */
    updateScreenReaderStepHint(): void {
        if (this.headScreenReaderHint) {
            this.headScreenReaderHint.textContent =
                this.getScreenReaderStepHintText();
        }
    }

    /**
     * Render curtain body with content.
     * @param {Array<ElementConfig | HTMLElement | Node | string>} contentNodes
     * @returns {Element}
     */
    renderBody(contentNodes: ContentNodes): DocumentFragment {
        const content = createElement({
            tagName: 'div',
            classList: {
                'ui-curtain__body-content': true,
            },
            children: contentNodes,
        });
        const titleId = `ui-curtain-title-${this._uuid}`;
        return html`
            <div
                role="region"
                aria-labelledby="${titleId}"
                aria-hidden="${!this.open}"
                class="ui-curtain__body"
            >
                ${content}
            </div>
        `;
    }

    /**
     * Renders head of the curtain.
     * @param { HTMLCollection } children header children
     */
    renderHead(children: HTMLCollection) {
        let iconElement: MaybeElement<UIIcon> =
            this.glyph && this.buildIconElement();
        let amountElement: MaybeElement<UIAmount>;

        const childrenArray: Array<HTMLElement> =
            children &&
            Array.from<HTMLElement>(children as Iterable<HTMLElement>);
        if (childrenArray?.length) {
            iconElement =
                iconElement ||
                extractSpecificElement<UIIcon>(childrenArray, 'ui-icon');
            amountElement = extractSpecificElement<UIAmount>(
                childrenArray,
                'ui-amount'
            );
        }

        if (iconElement instanceof UIIcon) {
            iconElement.classList.add('ui-curtain__head-icon');
            this.glyph = iconElement.glyph;
        }

        const isSpoiler = this.isSpoiler();
        const isNumbered = this.isNumbered();

        const titleId = `ui-curtain-title-${this._uuid}`;

        return element`
            <summary
                class="ui-curtain__head"
                role="button"
                aria-expanded="${this.open}"
                aria-disabled="${this.limited || this.isDisabledSpoiler()}"
                aria-hidden="${this.isHeadHidden()}"
            >
                ${!isSpoiler && iconElement}

                <div class="ui-curtain__head-content">
                    ${isSpoiler && this.buildChevronElement()}
                    ${isNumbered && this.buildScreenReaderStepHintElement()}
                    <span
                        id="${titleId}"
                        class="ui-curtain__head-title"
                    >${this.getTitle()}</span>
                    ${
                        !isSpoiler &&
                        !!childrenArray?.length &&
                        html`
                            <div class="ui-curtain__head-actions">
                                ${childrenArray.map((child) => html`${child}`)}
                            </div>
                        `
                    }
                </div>

                ${!isSpoiler && amountElement}
                ${!isSpoiler && this.buildChevronElement()}
            </summary>
        `;
    }

    /**
     * Changes current view to active (open) state.
     * @returns {UICurtain}
     */
    activate(): UICurtain {
        this.head.focus();
        /* istanbul ignore if */
        if (this.open) {
            return this;
        }

        /* istanbul ignore if */
        if (this.isAnimated()) {
            this.body.style.overflow = 'hidden';
            this.body.style.height = '0px';
        }

        this.open = true;
        return this;
    }

    /**
     * Sliding up animation
     */
    /* istanbul ignore next */
    slideUp(): void {
        this.isClosing = true;

        this.body.style.height = 'auto';
        this.body.style.visibility = 'visible';

        const startHeight = `${this.body.offsetHeight}px`;
        const endHeight = '0px';

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.body.animate(
            { height: [startHeight, endHeight] },
            {
                duration: getCSSPropertyValue('--ui-transition-time-slide'),
                easing: String(getCSSPropertyValue('--ui-transition-function')),
            }
        );

        this.animation.onfinish = () => {
            this.onAnimationFinish(false);
            this.details.open = false;
            this.headTitle.innerText = this.getTitle();
        };
        this.animation.oncancel = () => (this.isClosing = false);
    }

    /**
     * Sliding down animation
     */
    /* istanbul ignore next */
    slideDown(): void {
        this.isExpanding = true;

        const startHeight = '0px';
        const endHeight = `${this.content.offsetHeight}px`;

        this.animation?.cancel();

        this.animation = this.body.animate(
            { height: [startHeight, endHeight] },
            {
                duration: getCSSPropertyValue('--ui-transition-time-slide'),
                easing: String(getCSSPropertyValue('--ui-transition-function')),
            }
        );

        this.animation.onfinish = () => {
            this.onAnimationFinish(true);
            this.headTitle.innerText = this.getTitle();
        };
        this.animation.oncancel = () => (this.isExpanding = false);
    }

    /**
     * Handling finishing of animation
     * @param {boolean} shouldOpen
     */
    onAnimationFinish(shouldOpen: boolean): void {
        this.dispatchCustomEvent(
            shouldOpen
                ? UICurtain.CustomEvents.CurtainOpen
                : UICurtain.CustomEvents.CurtainClose
        );
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.body.removeAttribute('style');
        if (this.autoscroll && shouldOpen) {
            this.handleAutoScroll();
        }
    }

    /**
     * Handles curtains scroll on mobile.
     * @private
     */
    async handleAutoScroll(): Promise<void> {
        const hasVerticalScroll =
            document.body.scrollHeight > document.body.clientHeight;
        if (browser.mobileMediaMatches() && hasVerticalScroll && this.open) {
            await scrollTo(this, 0);
        }
    }

    /**
     * Handles toggling with animation
     * @private
     * @param {boolean} shouldOpen
     */
    handleAnimatedToggle(shouldOpen: boolean): void {
        this.body.style.overflow = 'hidden';
        if (this.isClosing || shouldOpen) {
            this.body.style.height = `${this.body.offsetHeight}px`;
            this.details.open = true;
            window.requestAnimationFrame(() => this.slideDown());
        } else if (this.isExpanding || !shouldOpen) {
            this.slideUp();
        }
    }

    /**
     * Handles toggling without animation
     * @private
     * @param {boolean} shouldOpen
     */
    handleStaticToggle(shouldOpen: boolean): void {
        this.details.open = shouldOpen;
        this.headTitle.innerText = this.getTitle();
        if (this.autoscroll && this.open) {
            this.handleAutoScroll();
        }
        this.dispatchCustomEvent(
            this.open
                ? UICurtain.CustomEvents.CurtainOpen
                : UICurtain.CustomEvents.CurtainClose
        );
    }

    /**
     * Handles summary (head) click.
     * @private
     * @param {Event} event
     */
    handleClick(event: Event): void {
        // Prevent default details toggling
        event?.preventDefault();

        // Do not toggle after clicked on link or button inside summary (head)
        const clickableTarget = closestByCond(
            event.target as Element,
            (node) => {
                return (
                    !!(node as Element).localName &&
                    UICurtain.clickableActions.includes(
                        (node as Element).localName
                    )
                );
            },
            (node) => {
                return !this.head.contains(node);
            }
        );
        if (clickableTarget) {
            return;
        }

        // Do not toggle after clicked on limited or disabled curtain/spoiler
        if (this.limited || this.isDisabledSpoiler()) {
            return;
        }

        // Otherwise - change the state open/close
        this.open = !this.open;

        // Legacy way: dispatch 'curtain-toggle' only after click
        this.dispatchCustomEvent(UICurtain.CustomEvents.CurtainToggle, null);
    }

    /**
     * Handle default <details> toggle
     * @private
     */
    handleToggle(e: Event): void {
        // Details component has feature (in Chrome), that it opens automatically,
        // if info was found by page-in search in browser inside content.
        // This should also trigger component update.

        if (this.details.open === this.hasAttribute('open')) {
            return;
        }

        // Should follow limited/disabled logic
        if (this.limited || this.isDisabledSpoiler()) {
            if (this.details.open) {
                this.details.open = false;
            }
            return;
        }

        if (this.details.open) {
            this.setAttribute('open', '');
        } else {
            this.removeAttribute('open');
        }
        this.dispatchCustomEvent(UICurtain.CustomEvents.CurtainToggle, null);
    }

    /**
     * Handles window resize.
     * @private
     */
    handleResize(e: Event): void {
        this.open = !browser.mobileMediaMatches();
        this.head.setAttribute(
            'aria-hidden',
            String(!browser.mobileMediaMatches())
        );
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    addEventListeners(): void {
        this.head.addEventListener('click', this.clickHandler!);
        this.details.addEventListener('toggle', this.toggleHandler!);
        /* istanbul ignore if */
        if (this.hasAttribute('mobile')) {
            browser.mobileMedia.addEventListener('change', this.resizeHandler!);
        }
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    removeEventListeners(): void {
        this.head.removeEventListener('click', this.clickHandler!);
        this.details.removeEventListener('toggle', this.toggleHandler!);
        /* istanbul ignore if */
        if (this.hasAttribute('mobile')) {
            browser.mobileMedia.removeEventListener(
                'change',
                this.resizeHandler!
            );
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        // Variable to format boolean attribute to the proper true/false values
        const newBoolean = newValue !== null;

        switch (name) {
            case 'label':
            case 'label-open':
            case 'label-close':
                this.headTitle.innerText = this.getTitle();
                break;
            case 'glyph':
                if (!newValue && this.headIcon) {
                    this.head.removeChild(this.headIcon);
                    break;
                }
                if (this.headIcon) {
                    this.headIcon.setAttribute('glyph', newValue!);
                } else {
                    this.head.insertBefore(
                        this.buildIconElement(),
                        this.headContent
                    );
                }
                break;
            case 'open':
                // If animation is needed, use animated toggling
                if (this.isAnimated()) {
                    /* istanbul ignore next */
                    this.handleAnimatedToggle(newBoolean);
                } else {
                    this.handleStaticToggle(newBoolean);
                }
                this.head.setAttribute('aria-expanded', String(newBoolean));
                this.body.setAttribute('aria-hidden', String(!newBoolean));
                break;
            case 'disabled':
            case 'limited':
                if (name === 'disabled' && !this.isSpoiler()) {
                    break;
                }
                this.head.setAttribute('aria-disabled', String(newBoolean));
                break;
            case 'hide-head':
                this.head.setAttribute('aria-hidden', String(newBoolean));
            // TODO: falls through - intentional?
            case 'mobile':
                this.head.setAttribute(
                    'aria-hidden',
                    String(newBoolean && !browser.mobileMediaMatches())
                );
                break;
            case 'index':
            case 'completed':
                if (this.isNumbered()) {
                    if (this.headScreenReaderHint) {
                        this.updateScreenReaderStepHint();
                    } else {
                        this.insertScreenReaderStepHint();
                    }
                } else {
                    this.removeScreenReaderStepHint();
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this._uuid = Digest.randomId();
        const childNodes: Array<HTMLElement> = this.detachChildNodes();
        const header: UndefinedOr<HTMLElement> = childNodes
            .filter((node) => (node as Element)?.localName === 'header')
            .shift();
        const section: UndefinedOr<HTMLElement> = childNodes
            .filter((node) => (node as Element)?.localName === 'section')
            .shift();

        const contentNodes: NodeListOf<ChildNode> | Array<HTMLElement> = section
            ? section.childNodes
            : childNodes.filter(
                  (node) => (node as Element)?.localName !== 'header'
              );

        if (this.isHeadHidden()) {
            this.open = true;
        }

        this.append(html`
            <details open=${this.open || null}>
                ${this.renderHead(header?.children as HTMLCollection)}
                ${this.renderBody(contentNodes as Array<HTMLElement>)}
            </details>
        `);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.clickHandler = this.handleClick.bind(this);
        this.toggleHandler = this.handleToggle.bind(this);
        this.resizeHandler = this.handleResize.bind(this);
        this.addEventListeners();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.removeEventListeners();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}
